import React from 'react';
import { Tooltip } from 'antd';
import { StarFilled, StarOutlined, DownOutlined, LoadingOutlined } from '@ant-design/icons';

interface Props {
	starred?: boolean;
	note?: string | null;
	size?: 'sm' | 'md' | 'lg';
	withDropdown?: boolean;
	// Hollow-on-dark variant for the priority-colored detail header: the unstarred pill
	// renders as a transparent pill with white outline + white star/chevron (matches the
	// sibling "+ tag" label button). Starred state keeps the gold pill, which reads on dark.
	onDark?: boolean;
	onClick?: (event: React.MouseEvent) => void;
	onStarClick?: (event: React.MouseEvent) => void;
	onDropdownClick?: (event: React.MouseEvent) => void;
	className?: string;
	title?: string;
	noteDisplay?: React.ReactNode;
	loading?: boolean;
}

const CIRCLE_SIZE_CLASS: Record<'sm' | 'md' | 'lg', string> = {
	sm: 'h-[22px] w-[22px]',
	md: 'h-[30px] w-[30px]',
	lg: 'h-10 w-10',
};

const ICON_SIZE_CLASS: Record<'sm' | 'md' | 'lg', string> = {
	sm: 'text-[14px]',
	md: 'text-sm',
	lg: 'text-xl',
};

const STARRED_CIRCLE_CLASS =
	'box-border inline-flex shrink-0 items-center justify-center rounded-full border border-solid !border-calendulaGold-5 !bg-calendulaGold-5';

const UNSTARRED_CIRCLE_CLASS =
	'box-border inline-flex shrink-0 items-center justify-center rounded-full border border-solid border-grey-5 bg-white';

// Unstarred circle on a dark header: white outline, transparent fill.
const UNSTARRED_ON_DARK_CIRCLE_CLASS =
	'box-border inline-flex shrink-0 items-center justify-center rounded-full border border-solid border-white bg-transparent relative left-[-1px]';

const STARRED_ICON_CLASS = '!text-white';
const UNSTARRED_ICON_CLASS = '!text-character-title-85';

const WorkOrderStarIcon: React.FC<Props> = ({
	starred = false,
	note,
	size = 'md',
	withDropdown = false,
	onDark = false,
	onClick,
	onStarClick,
	onDropdownClick,
	className,
	title,
	noteDisplay,
	loading = false,
}) => {
	const hasNote = withDropdown && starred && !!note;
	const hollowOnDark = onDark && !starred;
	const StarIcon: any = starred ? StarFilled : StarOutlined;
	const starHandler = onStarClick || onClick;
	const interactiveStar = !!starHandler && !loading;
	const interactiveDropdown = !!onDropdownClick && !loading;
	const tooltipText = title || (starred ? 'Untrack' : 'Track - only for me');
	const circleSizeClass = CIRCLE_SIZE_CLASS[size];
	const iconSizeClass = ICON_SIZE_CLASS[size];
	const unstarredIconClass = hollowOnDark ? STARRED_ICON_CLASS : UNSTARRED_ICON_CLASS;
	const starIconClass = `${iconSizeClass} ${starred ? STARRED_ICON_CLASS : unstarredIconClass}`;
	const unstarredCircleClass = hollowOnDark
		? UNSTARRED_ON_DARK_CIRCLE_CLASS
		: UNSTARRED_CIRCLE_CLASS;
	const starCircleClass = starred ? STARRED_CIRCLE_CLASS : unstarredCircleClass;
	// Chevron colour: white on the hollow-on-dark (unstarred) pill, dark otherwise.
	const dropdownIconClass = hollowOnDark ? '!text-white' : '!text-character-title-85';

	const handleStarClick = (event: React.MouseEvent) => {
		if (!starHandler) return;
		event.stopPropagation();
		event.preventDefault();
		starHandler(event);
	};

	const handleDropdownClick = (event: React.MouseEvent) => {
		if (!onDropdownClick) return;
		event.stopPropagation();
		event.preventDefault();
		onDropdownClick(event);
	};

	const starCircle = (
		<span
			role={interactiveStar ? 'button' : undefined}
			tabIndex={interactiveStar ? 0 : -1}
			aria-pressed={starred}
			onClick={handleStarClick}
			className={`${starCircleClass} ${circleSizeClass} ${
				interactiveStar ? 'cursor-pointer' : 'cursor-default'
			}`}
		>
			{loading ? (
				<LoadingOutlined spin className={starIconClass} />
			) : (
				<StarIcon className={starIconClass} />
			)}
		</span>
	);

	const starWithTooltip = (
		<Tooltip
			title={tooltipText}
			placement="top"
			arrowPointAtCenter
			align={{ offset: [0, 12] }}
			autoAdjustOverflow={{ adjustX: 1, adjustY: 0 }}
			// The tooltip sits just above the circle and slightly overlaps its top edge.
			// Without this it intercepts the cursor over the top of the circle (default
			// cursor / no click); pointer-events:none lets cursor + clicks pass through.
			overlayStyle={{ pointerEvents: 'none' }}
		>
			{starCircle}
		</Tooltip>
	);

	if (!withDropdown) {
		return (
			<span className={`inline-flex shrink-0${className ? ` ${className}` : ''}`}>
				{starWithTooltip}
			</span>
		);
	}

	return (
		<span
			className={`box-border inline-flex h-[30px] shrink-0 items-center rounded-[36px] ${
				hasNote
					? 'gap-1 bg-calendulaGold-1 pr-2'
					: hollowOnDark
					? 'w-[57px] border border-solid border-white bg-transparent'
					: 'w-[57px] bg-grey-3'
			}${className ? ` ${className}` : ''}`}
		>
			{/* Star side — the circle (and its full area) toggles the star, with a pointer
			    cursor. Sized to just the circle so the gap toward the chevron belongs to the
			    dropdown zone. The inner circle keeps its own handler (stopPropagation) for the
			    no-dropdown variant. */}
			<span
				onClick={handleStarClick}
				className={`inline-flex h-full shrink-0 items-center ${
					interactiveStar ? 'cursor-pointer' : ''
				}`}
			>
				{starWithTooltip}
			</span>
			{/* Dropdown side — claims everything to the right of the circle (the gap + chevron,
			    plus the note when present) so a click just left of the chevron opens the
			    dropdown rather than toggling the star. */}
			<span
				role={interactiveDropdown ? 'button' : undefined}
				tabIndex={interactiveDropdown ? 0 : -1}
				onClick={handleDropdownClick}
				className={`inline-flex h-full min-w-0 flex-1 items-center ${
					interactiveDropdown ? 'cursor-pointer' : ''
				} ${hasNote ? 'gap-1' : 'justify-center'}`}
			>
				{hasNote ? (
					<span className="!bodyregular max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap !text-grey-8">
						{noteDisplay != null ? noteDisplay : note}
					</span>
				) : null}
				<span className="inline-flex shrink-0 items-center justify-center">
					<DownOutlined className={`text-xs ${dropdownIconClass}`} />
				</span>
			</span>
		</span>
	);
};

export default WorkOrderStarIcon;
